/**
 * Timezone options for the business-brief `timezone` field.
 *
 * The selector shows a curated set of common business timezones pinned at the
 * top, followed by the full IANA timezone database. The value is stored as-is
 * and is not interpreted downstream, so the abbreviations and IANA identifiers
 * can coexist in the same list.
 *
 * Consumed by:
 * - the dashboard UI schema (`@doneverse/data`) to render the dropdown
 * - the API validation schema (below) to accept submissions
 */
/** Common business timezones, pinned to the top of the selector. */
export const CURATED_TIMEZONES = [
    'EST',
    'CST',
    'MST',
    'PST',
    'GMT',
    'CET',
    'IST',
    'SGT',
    'AEST',
];
/**
 * Full IANA timezone identifiers (e.g. `America/New_York`).
 *
 * `Intl.supportedValuesOf` is available in Node 18+ and every modern browser.
 * The empty fallback degrades gracefully to just the curated list if a runtime
 * somehow lacks it.
 */
function listIanaTimezones() {
    const supportedValuesOf = Intl.supportedValuesOf;
    try {
        return typeof supportedValuesOf === 'function' ? supportedValuesOf('timeZone') : [];
    }
    catch {
        return [];
    }
}
export const IANA_TIMEZONES = listIanaTimezones();
/**
 * Legacy / normalized values from earlier Typeform versions that may exist on
 * stored briefs. They must keep validating even though they are no longer
 * offered in the selector.
 */
export const LEGACY_TIMEZONE_OPTIONS = [
    'United States / Canada',
    'UK / Europe',
    'Australia / New Zealand',
];
/** Options offered in the selector: curated common zones first, then all IANA zones. */
export const BUSINESS_BRIEF_TIMEZONE_OPTIONS = [
    ...new Set([...CURATED_TIMEZONES, ...IANA_TIMEZONES]),
];
/** Whether the runtime recognizes `value` as a real IANA timezone identifier. */
function isValidIanaTimezone(value) {
    try {
        // Throws RangeError for unknown time zones. Accepts canonical names and
        // aliases, so it is a superset of whatever `supportedValuesOf` enumerated.
        new Intl.DateTimeFormat('en-US', { timeZone: value });
        return true;
    }
    catch {
        return false;
    }
}
/**
 * Whether a stored/submitted timezone value is acceptable.
 *
 * Used for API validation instead of matching against an enumerated option list:
 * the IANA set returned by `Intl.supportedValuesOf` varies by runtime/tzdata, so
 * a zone the browser offers might not appear in the API's enumeration. Checking
 * IANA validity directly avoids rejecting otherwise-legitimate selections.
 */
export function isAcceptedTimezone(value) {
    return (CURATED_TIMEZONES.includes(value) ||
        LEGACY_TIMEZONE_OPTIONS.includes(value) ||
        isValidIanaTimezone(value));
}
//# sourceMappingURL=business-brief-timezones.js.map